/**
 * Pass 19 — HypePets elemental type system.
 *
 * 13 elements. Each species has a required primary element + optional
 * secondary. Dual-typing applies the multiplier from BOTH defender
 * types (Pokémon model): an attacker hitting a dual-type defender
 * computes `eff(att, defPrimary) * eff(att, defSecondary)`. Range:
 * 0.25× (double-resisted) → 4× (double super-effective). Pure-single-
 * type defenders stay clamped to 0.5× / 1× / 2×.
 *
 * Multipliers are intentionally swingy (2.0× / 0.5×) so type matchups
 * are obvious in chat replies and feel decisive in PvP. Idle battle
 * still smooths variance over many rounds.
 */

export const HYPEEGG_ELEMENTS = [
  'fire',
  'water',
  'grass',
  'earth',
  'electric',
  'ice',
  'wind',
  'shadow',
  'light',
  'spirit',
  'hype',
  'chaos',
  'glitch',
] as const;

export type HypeEggElement = (typeof HYPEEGG_ELEMENTS)[number];

export interface ElementMeta {
  emoji: string;
  /** Tailwind-style accent color for badges; consumed by dashboard +
   *  overlay components for the per-element rim glow. */
  color: string;
  bg: string;
  label: string;
  flavor: string;
}

export const ELEMENT_META: Record<HypeEggElement, ElementMeta> = {
  fire: {
    emoji: '🔥',
    color: '#fb7185',
    bg: 'rgba(251, 113, 133, 0.18)',
    label: 'Fire',
    flavor: 'Sears through plants + frost, smothered by stone + tide.',
  },
  water: {
    emoji: '💧',
    color: '#38bdf8',
    bg: 'rgba(56, 189, 248, 0.18)',
    label: 'Water',
    flavor: 'Douses flame + erodes earth, conducts a shock.',
  },
  grass: {
    emoji: '🌿',
    color: '#34d399',
    bg: 'rgba(52, 211, 153, 0.18)',
    label: 'Grass',
    flavor: 'Drinks tides + cracks bedrock, scorched by flame + chill.',
  },
  earth: {
    emoji: '🪨',
    color: '#a3a3a3',
    bg: 'rgba(163, 163, 163, 0.22)',
    label: 'Earth',
    flavor: 'Grounds lightning + smothers fire, washed away by tide + root.',
  },
  electric: {
    emoji: '⚡',
    color: '#facc15',
    bg: 'rgba(250, 204, 21, 0.20)',
    label: 'Electric',
    flavor: 'Lights up water + storms, grounded by earth + hype.',
  },
  ice: {
    emoji: '❄️',
    color: '#bae6fd',
    bg: 'rgba(186, 230, 253, 0.20)',
    label: 'Ice',
    flavor: 'Freezes wind + withers grass, melts under fire.',
  },
  wind: {
    emoji: '💨',
    color: '#cbd5e1',
    bg: 'rgba(203, 213, 225, 0.20)',
    label: 'Wind',
    flavor: 'Scatters earth + spirit, snuffed by lightning + frost.',
  },
  shadow: {
    emoji: '🌑',
    color: '#a78bfa',
    bg: 'rgba(167, 139, 250, 0.20)',
    label: 'Shadow',
    flavor: 'Devours light + spirit, banished by light + hype.',
  },
  light: {
    emoji: '✨',
    color: '#fde68a',
    bg: 'rgba(253, 230, 138, 0.22)',
    label: 'Light',
    flavor: 'Pierces shadow + dispels spirit, refracts off chaos.',
  },
  spirit: {
    emoji: '👻',
    color: '#c4b5fd',
    bg: 'rgba(196, 181, 253, 0.22)',
    label: 'Spirit',
    flavor: 'Phases through shadow + chaos, blown by wind + dispersed by light.',
  },
  hype: {
    emoji: '🤘',
    color: '#f472b6',
    bg: 'rgba(244, 114, 182, 0.22)',
    label: 'Hype',
    flavor: 'Amplifies fire + lightning, banishes shadow — short-circuits on chaos + glitch.',
  },
  chaos: {
    emoji: '🌀',
    color: '#fb923c',
    bg: 'rgba(251, 146, 60, 0.22)',
    label: 'Chaos',
    flavor: 'Warps light + glitch + hype, splinters on spirit.',
  },
  glitch: {
    emoji: '📺',
    color: '#22d3ee',
    bg: 'rgba(34, 211, 238, 0.20)',
    label: 'Glitch',
    flavor: 'Corrupts grass + spirit + hype, voided by electricity + chaos.',
  },
};

/**
 * Single-axis type chart. Lookup as `EFFECTIVENESS[attacker][defender]`.
 * 2.0 = super effective, 1.0 = neutral, 0.5 = resisted. Each attacker
 * has exactly 2–3 super-effective and 2–3 resisted matchups; no
 * immunities (0×) so every battle resolves cleanly.
 */
export const EFFECTIVENESS: Record<HypeEggElement, Record<HypeEggElement, number>> = {
  fire: {
    fire: 1.0,
    water: 0.5,
    grass: 2.0,
    earth: 0.5,
    electric: 1.0,
    ice: 2.0,
    wind: 1.0,
    shadow: 1.0,
    light: 1.0,
    spirit: 1.0,
    hype: 1.0,
    chaos: 1.0,
    glitch: 2.0,
  },
  water: {
    fire: 2.0,
    water: 1.0,
    grass: 0.5,
    earth: 2.0,
    electric: 1.0,
    ice: 1.0,
    wind: 1.0,
    shadow: 1.0,
    light: 1.0,
    spirit: 1.0,
    hype: 1.0,
    chaos: 1.0,
    glitch: 0.5,
  },
  grass: {
    fire: 0.5,
    water: 2.0,
    grass: 1.0,
    earth: 2.0,
    electric: 1.0,
    ice: 0.5,
    wind: 1.0,
    shadow: 1.0,
    light: 1.0,
    spirit: 1.0,
    hype: 1.0,
    chaos: 1.0,
    glitch: 0.5,
  },
  earth: {
    fire: 2.0,
    water: 0.5,
    grass: 0.5,
    earth: 1.0,
    electric: 2.0,
    ice: 1.0,
    wind: 0.5,
    shadow: 1.0,
    light: 1.0,
    spirit: 1.0,
    hype: 1.0,
    chaos: 1.0,
    glitch: 1.0,
  },
  electric: {
    fire: 1.0,
    water: 2.0,
    grass: 1.0,
    earth: 0.5,
    electric: 1.0,
    ice: 1.0,
    wind: 2.0,
    shadow: 1.0,
    light: 1.0,
    spirit: 1.0,
    hype: 0.5,
    chaos: 1.0,
    glitch: 2.0,
  },
  ice: {
    fire: 0.5,
    water: 1.0,
    grass: 2.0,
    earth: 1.0,
    electric: 1.0,
    ice: 1.0,
    wind: 2.0,
    shadow: 1.0,
    light: 1.0,
    spirit: 1.0,
    hype: 1.0,
    chaos: 1.0,
    glitch: 1.0,
  },
  wind: {
    fire: 1.0,
    water: 1.0,
    grass: 1.0,
    earth: 2.0,
    electric: 0.5,
    ice: 0.5,
    wind: 1.0,
    shadow: 1.0,
    light: 1.0,
    spirit: 2.0,
    hype: 1.0,
    chaos: 1.0,
    glitch: 1.0,
  },
  shadow: {
    fire: 1.0,
    water: 1.0,
    grass: 1.0,
    earth: 1.0,
    electric: 1.0,
    ice: 1.0,
    wind: 1.0,
    shadow: 0.5,
    light: 2.0,
    spirit: 2.0,
    hype: 0.5,
    chaos: 1.0,
    glitch: 1.0,
  },
  light: {
    fire: 1.0,
    water: 1.0,
    grass: 1.0,
    earth: 1.0,
    electric: 1.0,
    ice: 1.0,
    wind: 1.0,
    shadow: 2.0,
    light: 0.5,
    spirit: 2.0,
    hype: 1.0,
    chaos: 0.5,
    glitch: 1.0,
  },
  spirit: {
    fire: 1.0,
    water: 1.0,
    grass: 1.0,
    earth: 1.0,
    electric: 1.0,
    ice: 1.0,
    wind: 0.5,
    shadow: 2.0,
    light: 0.5,
    spirit: 1.0,
    hype: 1.0,
    chaos: 2.0,
    glitch: 0.5,
  },
  hype: {
    fire: 2.0,
    water: 1.0,
    grass: 1.0,
    earth: 1.0,
    electric: 2.0,
    ice: 1.0,
    wind: 1.0,
    shadow: 2.0,
    light: 1.0,
    spirit: 1.0,
    hype: 1.0,
    chaos: 0.5,
    glitch: 0.5,
  },
  chaos: {
    fire: 1.0,
    water: 1.0,
    grass: 1.0,
    earth: 1.0,
    electric: 1.0,
    ice: 1.0,
    wind: 1.0,
    shadow: 1.0,
    light: 2.0,
    spirit: 0.5,
    hype: 2.0,
    chaos: 1.0,
    glitch: 2.0,
  },
  glitch: {
    fire: 1.0,
    water: 1.0,
    grass: 2.0,
    earth: 1.0,
    electric: 0.5,
    ice: 1.0,
    wind: 1.0,
    shadow: 1.0,
    light: 1.0,
    spirit: 2.0,
    hype: 2.0,
    chaos: 0.5,
    glitch: 1.0,
  },
};

export interface DefenderTypes {
  primary: HypeEggElement;
  secondary?: HypeEggElement | null;
}

/** Multiplies effectiveness across both defender types. Returns the
 *  combined multiplier (e.g. 4× for double super-effective, 0.25× for
 *  double-resisted). Single-typed defenders see just the primary chart
 *  value. */
export function getEffectiveness(attacker: HypeEggElement, defender: DefenderTypes): number {
  const row = EFFECTIVENESS[attacker];
  const primary = row[defender.primary] ?? 1;
  const secondary =
    defender.secondary && defender.secondary !== defender.primary
      ? (row[defender.secondary] ?? 1)
      : 1;
  return primary * secondary;
}

/** UI-friendly bucket for the effectiveness multiplier. Drives the
 *  "Super effective!" / "Not very effective…" chat + overlay labels. */
export type EffectivenessLabel = 'super' | 'normal' | 'resisted';

export function effectivenessLabel(multiplier: number): EffectivenessLabel {
  if (multiplier >= 1.5) return 'super';
  if (multiplier <= 0.75) return 'resisted';
  return 'normal';
}
